import { isEmpty, isNil, maxBy } from 'lodash';

import { YardTaskCheckLocationOutcome } from '@nova/core';

import type { CheckTaskCheckAssetContainerView } from '@nova/core';

function outcomeTime(row: CheckTaskCheckAssetContainerView): number {
  if (isNil(row.createDateTime)) {
    return Number.NEGATIVE_INFINITY;
  }
  const parsed = Date.parse(String(row.createDateTime));
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

/**
 * Returns the latest check outcome row from a list, picking by `createDateTime` ascending.
 * Rows with the same (or missing) timestamps fall back to input order (lodash `maxBy` keeps
 * the **first** match on ties; we walk back-to-front to preserve the "last entry wins" rule
 * used elsewhere — see `getCheckOutcomeChipDisplay`).
 */
export function pickLatestCheckOutcome(
  rows: ReadonlyArray<CheckTaskCheckAssetContainerView> | null | undefined
): CheckTaskCheckAssetContainerView | null {
  if (isEmpty(rows)) {
    return null;
  }
  const reversed = [...(rows ?? [])].reverse();
  return maxBy(reversed, outcomeTime) ?? null;
}

export function isLatestCheckMissing(
  rows: ReadonlyArray<CheckTaskCheckAssetContainerView> | null | undefined
): boolean {
  return pickLatestCheckOutcome(rows)?.outcome === YardTaskCheckLocationOutcome.MISSING;
}

/**
 * Filter the per-asset rows of an inventory task by a target `assetContainerId`.
 * Useful when handing a slice to the single-asset check wizard.
 */
export function filterCheckOutcomesByAsset(
  rows: ReadonlyArray<CheckTaskCheckAssetContainerView> | null | undefined,
  assetContainerId: string | null | undefined
): CheckTaskCheckAssetContainerView[] {
  if (isEmpty(rows) || isNil(assetContainerId) || isEmpty(assetContainerId)) {
    return [];
  }
  return (rows ?? []).filter(row => row.assetContainerId === assetContainerId);
}
